import type { AssessmentType, SubmittedAnswer } from '../apiClient';

/**
 * Framework-agnostic submission + idempotency lifecycle for the free journey.
 *
 * The submissionId is one UUID per intentional assessment journey. It is generated
 * once, sent on the authenticated submit, preserved with the anonymous pending
 * answers, and reused for the /welcome claim and any retry — so retries and the
 * claim are idempotent server-side. A new id is minted only for a new journey/retake.
 *
 * Storage is injected (a Pick<Storage>) so this is unit-testable without a DOM and
 * so components can pass sessionStorage.
 */
export type SessionStorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

const PENDING_KEY = 'pendingAssessment';

export interface PendingAssessment {
  type: AssessmentType;
  submissionId: string;
  answers: SubmittedAnswer[];
}

const ASSESSMENT_TYPES: AssessmentType[] = ['bottleneck', 'work_fit', 'perspective'];

/** New idempotency key for a brand-new assessment journey or an intentional retake. */
export function newSubmissionId(): string {
  return crypto.randomUUID();
}

/** Turn the questionId → value answer map into the wire array. */
export function formatAnswers(answers: Record<string, number>): SubmittedAnswer[] {
  return Object.entries(answers).map(([questionId, value]) => ({ questionId, value }));
}

/** All questions answered? (count-based; the fixed bank has unique ids.) */
export function isComplete(answers: Record<string, number>, total: number): boolean {
  return Object.keys(answers).length === total;
}

export function savePendingAssessment(storage: SessionStorageLike, pending: PendingAssessment): void {
  storage.setItem(PENDING_KEY, JSON.stringify(pending));
}

export function clearPendingAssessment(storage: SessionStorageLike): void {
  storage.removeItem(PENDING_KEY);
}

/** Read + validate the stored pending payload; returns null if absent or malformed. */
export function readPendingAssessment(storage: SessionStorageLike): PendingAssessment | null {
  const raw = storage.getItem(PENDING_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<PendingAssessment>;
    if (
      parsed &&
      typeof parsed.submissionId === 'string' &&
      typeof parsed.type === 'string' &&
      ASSESSMENT_TYPES.includes(parsed.type as AssessmentType) &&
      Array.isArray(parsed.answers)
    ) {
      return {
        type: parsed.type as AssessmentType,
        submissionId: parsed.submissionId,
        answers: parsed.answers as SubmittedAnswer[],
      };
    }
  } catch {
    // fall through to null on malformed JSON
  }
  return null;
}
